import {
    Editor,
    Element,
    Node,
    Path,
    Point,
    Range,
    Span,
    Transforms,
    type ListElement,
    type ListItemElement,
    type ListItemTextElement,
    type Location,
    type NodeEntry,
} from "slate";

import {getPreviousSibling} from "./node-helpers.ts";

// NOTE(reid): A lot of these helpers were adapted from here:
// https://github.com/prezly/slate/tree/main/packages/slate-lists

export const isSelectionWithinList = (editor: Editor) => {
    const [list] = Editor.nodes(editor, {
        match: isListNode,
    });
    return !!list;
};

export const isListNode = (n: Node): n is ListElement =>
    Element.isElement(n) &&
    (n.type === "bulleted-list" ||
        n.type === "numbered-list" ||
        n.type === "lettered-list");

export const isListItemNode = (n: Node): n is ListItemElement =>
    Element.isElement(n) && n.type === "list-item";

export const isListItemTextNode = (n: Node): n is ListItemTextElement =>
    Element.isElement(n) && n.type === "list-item-text";

const createListNode = (
    type: ListElement["type"],
    props = {},
): ListElement => ({
    type,
    children: [createListItemNode()],
    ...props,
});

export const createListItemNode = (props = {}): ListItemElement => ({
    type: "list-item",
    children: [createListItemTextNode()],
    ...props,
});

export const createListItemTextNode = (props = {}): ListItemTextElement => ({
    type: "list-item-text",
    children: [{text: ""}],
    ...props,
});

export const getListType = (n: Node): ListElement["type"] =>
    isListNode(n) ? n.type : "bulleted-list";

export const isConvertibleToListTextNode = (node: Node) => {
    return (
        Element.isElement(node) &&
        (node.type === "paragraph" ||
            node.type === "heading-one" ||
            node.type === "heading-two" ||
            node.type === "heading-three" ||
            node.type === "block-quote")
    );
};

export const getParentListItem = (
    editor: Editor,
    path: Path,
): NodeEntry<Element> | null => {
    const parentListItem = Editor.above(editor, {
        at: path,
        match: isListItemNode,
    });

    return parentListItem ?? null;
};

/**
 * Returns "list" node nested in "list-item" at a given path.
 * Returns null if there is no nested "list".
 */
export const getNestedList = (
    editor: Editor,
    path: Path,
): NodeEntry<Element> | null => {
    const nestedListPath = [...path, 1];

    if (!Node.has(editor, nestedListPath)) {
        return null;
    }

    const nestedList = Node.get(editor, nestedListPath);

    if (Element.isElement(nestedList) && isListNode(nestedList)) {
        return [nestedList, nestedListPath];
    }

    return null;
};

/**
 * Moves all "list-items" from one "list" to the end of another "list".
 */
export const moveListItemsToAnotherList = (
    editor: Editor,
    parameters: {at: NodeEntry<Node>; to: NodeEntry<Node>},
): boolean => {
    const [sourceListNode, sourceListPath] = parameters.at;
    const [targetListNode, targetListPath] = parameters.to;

    if (
        Element.isElement(sourceListNode) &&
        Element.isElement(targetListNode) &&
        isListNode(sourceListNode) &&
        isListNode(targetListNode) &&
        sourceListNode.children.length > 0
    ) {
        for (let i = 0; i < sourceListNode.children.length; ++i) {
            Transforms.moveNodes(editor, {
                at: [...sourceListPath, 0],
                to: [...targetListPath, targetListNode.children.length + i],
            });
        }

        return true;
    }

    return false;
};

/**
 * Nests (moves) given "list" in a given "list-item".
 */
export function moveListToListItem(
    editor: Editor,
    parameters: {
        at: NodeEntry<Node>;
        to: NodeEntry<Node>;
    },
): void {
    const [sourceListNode, sourceListPath] = parameters.at;
    const [targetListNode, targetListPath] = parameters.to;

    if (!isListNode(sourceListNode) || !isListItemNode(targetListNode)) {
        return;
    }

    Transforms.moveNodes(editor, {
        at: sourceListPath,
        to: [...targetListPath, 1],
    });
}

export const mergeListWithPreviousSiblingList = (
    editor: Editor,
    [node, path]: NodeEntry<Node>,
): boolean => {
    if (!isListNode(node)) {
        return false;
    }

    const previousSibling = getPreviousSibling(editor, path);
    if (!previousSibling) {
        return false;
    }

    const [previousSiblingNode, _] = previousSibling;
    // Allow merging if the previous sibling is a list, regardless of type
    if (isListNode(previousSiblingNode)) {
        Transforms.mergeNodes(editor, {at: path});
        return true;
    }

    return false;
};

/**
 * Increases nesting depth of all "list-items" in the current selection.
 * All nodes matching options.wrappableTypes in the selection will be converted to "list-items" and wrapped in a "list".
 *
 * @returns {boolean} True, if the editor state has been changed.
 */
export const increaseListDepth = (
    editor: Editor,
    at: Location | null = editor.selection,
    depth: number = 1,
    type: ListElement["type"],
): boolean => {
    if (!at) {
        return false;
    }
    const listItems = getListItems(editor, at);
    const indentableListItems = listItems.filter(([, listItemPath]) => {
        const previousListItem = getPreviousSibling(editor, listItemPath);
        return previousListItem !== null;
    });

    if (indentableListItems.length === 0) {
        return false;
    }

    const refs = pickSubtreesRoots(indentableListItems).map(([_, path]) =>
        Editor.pathRef(editor, path),
    );

    // Round down the depth if it isn't a positive integer
    depth = Math.floor(Math.max(0, depth));

    Editor.withoutNormalizing(editor, () => {
        wrapInList(editor, type); // Use the new type parameter here

        refs.forEach((ref) => {
            if (ref.current) {
                for (let i = 0; i < depth; i++) {
                    increaseListItemDepth(editor, ref.current, type);
                }
            }
        });
    });

    return true;
};

/**
 * Increases nesting depth of "list-item" at a given Path.
 */
export const increaseListItemDepth = (
    editor: Editor,
    listItemPath: Path,
    type: ListElement["type"],
): boolean => {
    const previousListItem = getPreviousSibling(editor, listItemPath);
    if (!previousListItem) {
        // The existence of previous "list-item" is necessary and sufficient for the operation to be possible.
        // See: https://en.wikipedia.org/wiki/Necessity_and_sufficiency
        return false;
    }

    const [previousListItemNode, previousListItemPath] = previousListItem;
    if (!isListItemNode(previousListItemNode)) {
        return false;
    }

    const previousListItemChildListPath = [...previousListItemPath, 1];
    const previousListItemHasChildList = Node.has(
        editor,
        previousListItemChildListPath,
    );

    let changed = false;

    Editor.withoutNormalizing(editor, () => {
        // Ensure there's a nested "list" in the previous sibling "list-item".
        if (!previousListItemHasChildList) {
            Transforms.insertNodes(
                editor,
                createListNode(type, {children: []}),
                {
                    at: previousListItemChildListPath,
                },
            );
            changed = true;
        }

        const previousListItemChildList = Node.get(
            editor,
            previousListItemChildListPath,
        );

        if (
            Element.isElement(previousListItemChildList) &&
            isListNode(previousListItemChildList)
        ) {
            const index = previousListItemHasChildList
                ? previousListItemChildList.children.length
                : 0;

            Transforms.moveNodes(editor, {
                at: listItemPath,
                to: [...previousListItemChildListPath, index],
            });

            changed = true;
        }
    });

    return changed;
};

/**
 * Decreases nesting depth of all "list-items" in the current selection.
 * All "list-items" in the root "list" will become "default" nodes.
 *
 * @returns {boolean} True, if the editor state has been changed.
 */
export const decreaseListDepth = (
    editor: Editor,
    at: Location | Span | null = editor.selection,
): boolean => {
    if (!at) {
        return false;
    }

    const listItems = getListItems(editor, at);

    // When calling `decreaseListItemDepth` the paths and references to "list-items"
    // can change, so we need a way of marking the "list-items" scheduled for transformation.
    const refs = pickSubtreesRoots(listItems).map(([_, path]) =>
        Editor.pathRef(editor, path),
    );

    let handled = false;

    refs.forEach((ref) => {
        if (ref.current) {
            handled = decreaseListItemDepth(editor, ref.current) || handled;
        }

        ref.unref();
    });

    return handled;
};

/**
 * Decreases nesting depth of "list-item" at a given Path.
 *
 * @returns {boolean} True, if the editor state has been changed.
 */
export const decreaseListItemDepth = (
    editor: Editor,
    listItemPath: Path,
): boolean => {
    const parentList = getParentList(editor, listItemPath);
    if (!parentList) {
        // It should never happen.
        return false;
    }

    const [parentListNode, parentListPath] = parentList;
    const parentListItem = getParentListItem(editor, listItemPath);
    const listItemIndex = listItemPath[listItemPath.length - 1];
    const previousSiblings = parentListNode.children.slice(0, listItemIndex);
    const nextSiblings = parentListNode.children.slice(listItemIndex + 1);

    Editor.withoutNormalizing(editor, () => {
        // We have to move all subsequent sibling "list-items" into a new "list" that will be
        // nested in the "list-item" we're trying to move.
        nextSiblings.forEach(() => {
            // The next sibling path is always the same, because once we move out the next sibling,
            // another one will take its place.
            const nextSiblingPath = [...parentListPath, listItemIndex + 1];
            increaseListItemDepth(
                editor,
                nextSiblingPath,
                getListType(parentListNode),
            );
        });

        if (parentListItem) {
            // Move the "list-item" to the grandparent "list".
            const [, parentListItemPath] = parentListItem;
            Transforms.moveNodes(editor, {
                at: listItemPath,
                to: Path.next(parentListItemPath),
            });

            // We've moved the "list-item" and all its subsequent sibling "list-items" out of this list.
            // So in case there are no more "list-items" left, we should remove the list.
            if (previousSiblings.length === 0) {
                Transforms.removeNodes(editor, {at: parentListPath});
            }
        } else {
            // Move the "list-item" to the root of the editor.
            const listItemTextPath = [...listItemPath, 0];
            const listItemNestedListPath = [...listItemPath, 1];

            if (Node.has(editor, listItemNestedListPath)) {
                Transforms.setNodes(
                    editor,
                    createListNode(getListType(parentListNode), {
                        children: [],
                    }),
                    {at: listItemNestedListPath},
                );
                Transforms.liftNodes(editor, {at: listItemNestedListPath});
                Transforms.liftNodes(editor, {at: Path.next(listItemPath)});
            }

            if (Node.has(editor, listItemTextPath)) {
                Transforms.setNodes(
                    editor,
                    {type: "paragraph"},
                    {at: listItemTextPath},
                );
                Transforms.liftNodes(editor, {at: listItemTextPath});
                Transforms.liftNodes(editor, {at: listItemPath});
            }
        }
    });

    return true;
};

/**
 * All nodes matching `isConvertibleToListTextNode` in the current selection
 * will be converted to list items and then wrapped in lists.
 */
export const wrapInList = (
    editor: Editor,
    listType: ListElement["type"],
): boolean => {
    const nonListEntries = Array.from(
        Editor.nodes(editor, {
            at: editor.selection || undefined,
            match: (node) => {
                return (
                    Element.isElement(node) &&
                    !isListNode(node) &&
                    !isListItemNode(node) &&
                    !isListItemTextNode(node) &&
                    isConvertibleToListTextNode(node)
                );
            },
        }),
    );

    if (nonListEntries.length === 0) {
        return false;
    }

    const refs = nonListEntries.map(([_, path]) =>
        Editor.pathRef(editor, path),
    );

    refs.forEach((ref) => {
        const path = ref.current;
        if (path) {
            Editor.withoutNormalizing(editor, () => {
                Transforms.setNodes(editor, createListItemTextNode(), {
                    at: path,
                });
                Transforms.wrapNodes(editor, createListItemNode(), {
                    at: path,
                });
                Transforms.wrapNodes(editor, createListNode(listType), {
                    at: path,
                });
            });
        }
        ref.unref();
    });

    return true;
};

export const unwrapList = (
    editor: Editor,
    at: Location | null = editor.selection,
): boolean => {
    if (!at) {
        return false;
    }

    let iterations = 0;

    const span = toSpan(at);
    const start = Editor.pathRef(editor, span[0]);
    const end = Editor.pathRef(editor, span[1]);

    Editor.withoutNormalizing(editor, () => {
        do {
            if (!start.current || !end.current) {
                break;
            }

            const location: Span = [start.current, end.current];
            const items = getListItems(editor, location);
            if (items.length === 0) {
                break;
            }

            // limit iterations just to prevent an accidental infinite loop
            iterations++;

            decreaseListDepth(editor, location);
        } while (iterations < 100);
    });

    start.unref();
    end.unref();

    return iterations > 0;
};

/**
 * Returns all "list-items" in a given Range.
 */
export const getListItems = (
    editor: Editor,
    at: Location | Span | null = editor.selection,
): NodeEntry<Element>[] => {
    if (!at) {
        return [];
    }

    const start = getLocationStart(at);
    const listItems = Editor.nodes(editor, {at, match: isListItemNode});

    return Array.from(listItems).filter(([, path]) => {
        const [, grandparent] = Path.ancestors(start, {reverse: true});
        const rangeIsGrandhild = Path.equals(path, grandparent);
        const rangeIsDescendant = Path.isDescendant(start, path);
        const rangeStartsAfter = Path.isAfter(start, path);

        if (rangeIsDescendant) {
            // There's just one case where we want to include a "list-item" that is
            // an ancestor of the range starting point - when it's a grandparent of that point.
            // It's because the selection starting point will be in a grandchild `{ text: '' }` node.
            // <h-li>                       <--- grandparent
            //     <h-li-text>
            //         <h-text>             <--- grandchild
            //             <anchor />       <--- range starting point
            //         </h-text>
            //     </h-li-text>
            // </h-li>
            // Other ancestors are not "visually" in selection, but they are returned by `Editor.nodes`.
            return !rangeStartsAfter && rangeIsGrandhild;
        }

        return !rangeStartsAfter;
    }) as NodeEntry<Element>[];
};

export const toggleList = (editor: Editor, listType: ListElement["type"]) => {
    const parentList = getParentList(
        editor,
        getLocationStart(editor.selection!),
    );

    if (!parentList) {
        // If there's no parent list, wrap selection in a list!
        wrapInList(editor, listType);
        return;
    }

    if (parentList[0].type !== listType) {
        // If there is a parent list but it has the wrong type (bulleted vs.
        // numbered), change the type of that parent list.
        Transforms.setNodes(editor, {type: listType}, {at: parentList[1]});
        return;
    }

    // If there is a list of the right type, "toggle it off" (unwrap it and set
    // the block type back to paragraph):
    decreaseListDepth(editor);
    Transforms.setNodes(editor, {type: "paragraph"});
};

/**
 * Will return a minimal subset of the given nodes that includes the rest of the given nodes as descendants.
 *
 * Example:
 *           ROOT
 *         /     \
 *        /       \
 *       A         B
 *     / | \     / | \
 *    A1 A2 A3  B1 B2 B3
 *   /       \         \
 *  A11      A31       B31
 *
 * A is the subtree root that includes every other node:
 * - pickSubtreesRoots([A, A1, A2, A3, A31]) === [A]
 *
 * A and B1 are the subtree roots that include every other node:
 * - pickSubtreesRoots([A, A1, A2, A3, A31, B1]) === [A, B1]
 *
 * A, B1, B2, and B3 are the subtree roots that include every other node:
 * - pickSubtreesRoots([A, A1, A2, A3, A31, B1, B2, B3, B31]) === [A, B1, B2, B3]
 *
 * A and B are the subtree roots that include every other node:
 * - pickSubtreesRoots([A, A1, A2, A3, A31, B, B1, B2, B3, B31]) === [A, B]
 */
const pickSubtreesRoots = (entries: NodeEntry<Node>[]): NodeEntry<Node>[] => {
    return entries.filter(([, nodePath]) => {
        const ancestors = Path.ancestors(nodePath);

        return !ancestors.some((ancestor) => {
            return entries.some(([, path]) => Path.equals(path, ancestor));
        });
    });
};

export const getParentList = (
    editor: Editor,
    at: Location,
): NodeEntry<Element> | null => {
    const parentList = Editor.above(editor, {
        at,
        match: isListNode,
    });

    return parentList ?? null;
};

const toSpan = (at: Location): Span => {
    if (Range.isRange(at)) {
        const [start, end] = Range.edges(at);
        return [start.path, end.path];
    }

    if (Point.isPoint(at)) {
        return [at.path, at.path];
    }

    return [at, at];
};

const getLocationStart = (location: Location | Span): Path => {
    if (Range.isRange(location)) {
        return Range.start(location).path;
    }
    if (Point.isPoint(location)) {
        return location.path;
    }
    if (Span.isSpan(location)) {
        const [start, end] = location;
        return Path.compare(start, end) <= 0 ? start : end;
    }
    return location;
};
